import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { tenantKey } from '@/lib/queryClient'
import { useTenant } from '@/hooks/useTenant'
import { prepaidApi } from './api'
import type { CreatePrepaidPayload } from './types'

export function usePrepaidSchedules() {
  const tenantId = useTenant()
  return useQuery({
    queryKey: tenantKey(tenantId, 'prepaid-schedules'),
    queryFn: prepaidApi.list,
  })
}

export function usePrepaidDetail(id: number | null) {
  const tenantId = useTenant()
  return useQuery({
    queryKey: tenantKey(tenantId, 'prepaid-schedules', id ?? 'none', 'detail'),
    queryFn: () => prepaidApi.detail(id!),
    enabled: id !== null,
  })
}

function invalidate(qc: ReturnType<typeof useQueryClient>, tenantId: string | null) {
  qc.invalidateQueries({ queryKey: tenantKey(tenantId, 'prepaid-schedules') })
  qc.invalidateQueries({ queryKey: tenantKey(tenantId, 'posting-status') })
}

export function useCreatePrepaid() {
  const qc = useQueryClient()
  const tenantId = useTenant()
  return useMutation({
    mutationFn: (body: CreatePrepaidPayload) => prepaidApi.create(body),
    onSuccess: () => invalidate(qc, tenantId),
  })
}

export function useUpdatePrepaid() {
  const qc = useQueryClient()
  const tenantId = useTenant()
  return useMutation({
    mutationFn: ({ id, body }: { id: number; body: Partial<CreatePrepaidPayload> }) =>
      prepaidApi.update(id, body),
    onSuccess: () => invalidate(qc, tenantId),
  })
}

export function useDeletePrepaid() {
  const qc = useQueryClient()
  const tenantId = useTenant()
  return useMutation({
    mutationFn: (id: number) => prepaidApi.remove(id),
    onSuccess: () => invalidate(qc, tenantId),
  })
}
